import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { facilitiesAndAmenitiesAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import { AdminPage, StateBlock } from '../Common/admin';

const NS = 'facilitiesAndAmenities.edit';

const FacilitiesAndAmenitiesEdit = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nameAr: '',
    nameEng: '',
    icon: '',
    isActive: true,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadRecord = useCallback(async () => {
    try {
      setLoadingData(true);
      const { data } = await facilitiesAndAmenitiesAPI.getById(id);
      setFormData({
        nameAr: data.nameAr || '',
        nameEng: data.nameEng || '',
        icon: data.icon || '',
        isActive: !!data.isActive,
      });
    } catch (err) {
      setErrorMessage(t(`${NS}.messages.loadError`));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const e = {};
    if (!formData.nameAr.trim()) e.nameAr = t(`${NS}.errors.nameArRequired`);
    if (!formData.nameEng.trim()) e.nameEng = t(`${NS}.errors.nameEngRequired`);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await facilitiesAndAmenitiesAPI.update(id, formData);
      setShowSuccessModal(true);
      setTimeout(() => navigate('/facilities-and-amenities'), 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t(`${NS}.messages.updateError`));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  const field = (name, labelKey, required, maxLength) => (
    <div className="ad-form-field">
      <label className={`ad-label ${required ? 'ad-label-required' : ''}`} htmlFor={name}>
        {t(`${NS}.fields.${labelKey}`)}
      </label>
      <input
        id={name}
        type="text"
        name={name}
        className={`ad-input ${errors[name] ? 'ad-input-error' : ''}`}
        value={formData[name]}
        onChange={handleChange}
        maxLength={maxLength}
      />
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  const backButton = (
    <button type="button" className="ad-btn" onClick={() => navigate('/facilities-and-amenities')}>
      <i className="fas fa-arrow-right"></i>
      {t(`${NS}.backToList`)}
    </button>
  );

  if (loadingData) {
    return (
      <AdminPage title={t(`${NS}.title`)} headerActions={backButton}>
        <StateBlock icon="fa-hourglass-half" title={t('common.loading')} />
      </AdminPage>
    );
  }

  return (
    <AdminPage title={t(`${NS}.title`)} headerActions={backButton}>
      <form onSubmit={handleSubmit}>
        <div className="ad-form">
          <div className="ad-form-grid">
            {field('nameAr', 'nameAr', true, 100)}
            {field('nameEng', 'nameEng', true, 100)}
            {field('icon', 'icon', false, 100)}
          </div>

          <div className="ad-form-section">
            <div className="ad-check-row">
              <input
                id="isActive"
                type="checkbox"
                name="isActive"
                className="ad-checkbox"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <label htmlFor="isActive">{t(`${NS}.fields.isActive`)}</label>
            </div>
          </div>
        </div>

        <div className="ad-form-foot">
          <button type="button" className="ad-btn" onClick={() => navigate('/facilities-and-amenities')}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="ad-btn ad-btn-primary" disabled={loading}>
            {loading ? t('common.loading') : t(`${NS}.save`)}
          </button>
        </div>
      </form>

      <SuccessModal
        show={showSuccessModal}
        message={t(`${NS}.messages.updateSuccess`)}
        onClose={() => setShowSuccessModal(false)}
      />
      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </AdminPage>
  );
};

export default FacilitiesAndAmenitiesEdit;
